import { Collections } from "@/types/branding";
import { useEffect, useRef, useState } from "react";

export function useCollectionsTagsSelect({
  collections,
  selectedCollections,
  onChange,
}: {
  selectedCollections: string[];
  onChange: (collections: string[]) => void;
  collections: Collections;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastInputMethodRef = useRef("mouse");
  const suppressNextInputFocusOpenRef = useRef(false);

  const collectionEntries = Object.entries(collections);
  const filteredEntries = collectionEntries.filter(([key, value]) => {
    const lcaseTerm = searchTerm.toLowerCase();
    const localizedValues = Object.values(value.localized_names);
    return (
      localizedValues.some((l) => l.toLowerCase().includes(searchTerm)) ||
      value.name.includes(searchTerm) ||
      key.toLowerCase().includes(lcaseTerm)
    );
  });

  const selectListNode = (position: "first" | "last") => {
    if (!dropdownRef?.current) return;
    const listBox = dropdownRef.current;
    const allOptions = listBox.querySelectorAll('button[role="option"]');
    const index = position === "first" ? 0 : allOptions.length - 1;
    const elementToSelect = allOptions[index] as HTMLElement;
    elementToSelect?.focus();
  };

  // Container events
  const handleOnContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest(".collections-tags-select__toggle")) {
      return;
    }
    setFocused(true);
    setIsOpen(true);
  };
  const handleOnContainerFocus = (event: React.FocusEvent) => {
    const buttonElement = event.target.closest("button");
    if (
      buttonElement &&
      buttonElement.className.includes("tag-remove") &&
      lastInputMethodRef.current === "mouse"
    ) {
      return;
    }
    setFocused(true);
  };
  const handleOnContainerBlur = (
    event: React.FocusEvent<HTMLDivElement, Element>,
  ) => {
    const focusedAfterBlurElement = event.relatedTarget as Node;
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(focusedAfterBlurElement)
    ) {
      setFocused(false);
      setIsOpen(false);
    }
  };
  const handleOnContainerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      inputRef?.current?.focus();
    }
  };
  // End container events

  // Input events
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === "Backspace" &&
      searchTerm === "" &&
      selectedCollections.length > 0
    ) {
      // Remove last selected item
      const lastItem = selectedCollections[selectedCollections.length - 1];
      handleRemove(lastItem, event);
    } else if (event.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    } else if (event.key === "Enter" && filteredEntries.length > 0) {
      event.preventDefault();
      // Select first filtered item
      const firstKey = filteredEntries[0][0];
      if (!selectedCollections.includes(firstKey)) {
        handleAdd(firstKey);
      }
    } else if (event.key === "ArrowDown") {
      selectListNode("first");
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleInputClick = () => setIsOpen(true);
  const handleInputFocus = () => {
    if (suppressNextInputFocusOpenRef.current) {
      suppressNextInputFocusOpenRef.current = false;
      return;
    }
    setIsOpen(true);
  };
  // End input events

  // List events
  const handleRemove = (key: string, event?: React.UIEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    onChange(selectedCollections.filter((k) => k !== key));
  };

  const handleAdd = (key: string) => {
    if (!selectedCollections.includes(key)) {
      onChange([...selectedCollections, key]);
    }
    setSearchTerm("");
    inputRef.current?.focus();
  };

  const handleOnListItemKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        const nextOption = event.currentTarget
          .nextElementSibling as HTMLElement;
        if (nextOption) {
          nextOption?.focus();
        } else {
          selectListNode("first");
        }
        event.preventDefault();
        event.stopPropagation();
        break;
      case "ArrowUp":
        const prevOption = event.currentTarget
          .previousElementSibling as HTMLElement;
        if (prevOption) {
          prevOption?.focus();
        } else {
          selectListNode("last");
        }
        event.preventDefault();
        event.stopPropagation();
        break;
      case "Escape":
        setIsOpen(false);
        setSearchTerm("");
        suppressNextInputFocusOpenRef.current = true;
        inputRef.current?.focus();
        event.preventDefault();
        event.stopPropagation();
        break;
    }
  };
  // End list events

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      lastInputMethodRef.current = "mouse";
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
        setFocused(false);
      }
    };

    const handleContainerKeyDown = () => {
      lastInputMethodRef.current = "keyboard";
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleContainerKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleContainerKeyDown);
    };
  }, []);

  useEffect(() => {
    if (inputRef == null) return;
    if (lastInputMethodRef == null) return;
    if (!focused) return;
    if (lastInputMethodRef.current === "mouse") inputRef?.current?.focus();
  }, [focused]);

  return {
    state: {
      isOpen,
      setIsOpen,
      focused,
      setFocused,
      searchTerm,
      setSearchTerm,
    },
    data: {
      collectionEntries,
      filteredEntries,
    },
    refs: {
      dropdownRef,
      inputRef,
      lastInputMethodRef,
    },
    containerEventHandlers: {
      handleOnContainerBlur,
      handleOnContainerClick,
      handleOnContainerKeyDown,
      handleOnContainerFocus,
    },
    inputEventHandlers: {
      handleInputClick,
      handleInputFocus,
      handleInputKeyDown,
    },
    listEventHandlers: {
      handleOnListItemKeyDown,
      handleAdd,
      handleRemove,
    },
  };
}
